import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Message,
} from 'semantic-ui-react';
import {
  API,
  showError,
  showSuccess,
  timestamp2string,
  verifyJSON,
} from '../helpers';

const OperationSetting = () => {
  const { t } = useTranslation();
  let now = new Date();
  const PLAN_CODES = ['free', 'standard', 'pro', 'max', 'ultra'];
  // 与后端 applyPlanOverride 读取的 key 保持一致：Plan<field>_<code>，价格字段是 PlanPrice_（单位：分）。
  const PLAN_FIELDS = ['Price', 'MonthlyQuota', 'RequestLimit5h', 'RequestLimitWeek', 'ConcurrencyLimit', 'QuotaExhaustStrategy', 'Models'];

  let [inputs, setInputs] = useState({
    QuotaForNewUser: 0,
    QuotaForInviter: 0,
    QuotaForInvitee: 0,
    QuotaRemindThreshold: 0,
    PreConsumedQuota: 0,
    ModelRatio: '',
    CompletionRatio: '',
    GroupRatio: '',
    TopUpLink: '',
    ChatLink: '',
    QuotaPerUnit: 0,
    AutomaticDisableChannelEnabled: '',
    AutomaticEnableChannelEnabled: '',
    ChannelDisableThreshold: 0,
    LogConsumeEnabled: '',
    DisplayInCurrencyEnabled: '',
    DisplayTokenStatEnabled: '',
    ApproximateTokenEnabled: '',
    RetryTimes: 0,
    SubscriptionEnabled: '',
    UsageDisplayMultiplier: '1.0',
    XunhuPayEnabled: '',
    XunhuPayAppID: '',
    XunhuPayAppSecret: '',
    XunhuPayNotifyURL: '',
    XunhuPayGateway: 'https://api.xunhupay.com',
  });
  const [originInputs, setOriginInputs] = useState({});
  let [loading, setLoading] = useState(false);
  let [historyTimestamp, setHistoryTimestamp] = useState(
    timestamp2string(now.getTime() / 1000 - 30 * 24 * 3600)
  ); // a month ago

  const getOptions = async () => {
    const res = await API.get('/api/option/');
    const { success, message, data } = res.data;
    if (success) {
      let newInputs = {};
      data.forEach((item) => {
        if (
          item.key === 'ModelRatio' ||
          item.key === 'GroupRatio' ||
          item.key === 'CompletionRatio'
        ) {
          item.value = JSON.stringify(JSON.parse(item.value), null, 2);
        }
        if (item.value === '{}') {
          item.value = '';
        }
        newInputs[item.key] = item.value;
      });
      setInputs(newInputs);
      setOriginInputs(newInputs);
    } else {
      showError(message);
    }
  };

  // V2：套餐模型白名单多选的数据源（复用 channel/models 接口）
  const [modelOptions, setModelOptions] = useState([]);

  useEffect(() => {
    getOptions().then();
    (async () => {
      try {
        // 用「已启用渠道配置的模型并集」（字符串数组），即「渠道配了什么就能选什么」
        const res = await API.get('/api/channel/enabled_models');
        const { success, data } = res.data;
        if (success && Array.isArray(data)) {
          setModelOptions(data.map((m) => ({ key: m, text: m, value: m })));
        }
      } catch (e) {
        // 模型列表加载失败不阻塞表单
      }
    })();
  }, []);

  const updateOption = async (key, value) => {
    setLoading(true);
    if (key.endsWith('Enabled')) {
      value = inputs[key] === 'true' ? 'false' : 'true';
    }
    const res = await API.put('/api/option/', {
      key,
      value,
    });
    const { success, message } = res.data;
    if (success) {
      setInputs((inputs) => ({ ...inputs, [key]: value }));
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const handleInputChange = async (e, { name, value }) => {
    if (name.endsWith('Enabled')) {
      await updateOption(name, value);
    } else {
      setInputs((inputs) => ({ ...inputs, [name]: value }));
    }
  };

  const submitConfig = async (group) => {
    switch (group) {
      case 'monitor':
        if (
          originInputs['ChannelDisableThreshold'] !==
          inputs.ChannelDisableThreshold
        ) {
          await updateOption(
            'ChannelDisableThreshold',
            inputs.ChannelDisableThreshold
          );
        }
        if (
          originInputs['QuotaRemindThreshold'] !== inputs.QuotaRemindThreshold
        ) {
          await updateOption(
            'QuotaRemindThreshold',
            inputs.QuotaRemindThreshold
          );
        }
        break;
      case 'ratio':
        if (originInputs['ModelRatio'] !== inputs.ModelRatio) {
          if (!verifyJSON(inputs.ModelRatio)) {
            showError('模型倍率不是合法的 JSON 字符串');
            return;
          }
          await updateOption('ModelRatio', inputs.ModelRatio);
        }
        if (originInputs['GroupRatio'] !== inputs.GroupRatio) {
          if (!verifyJSON(inputs.GroupRatio)) {
            showError('分组倍率不是合法的 JSON 字符串');
            return;
          }
          await updateOption('GroupRatio', inputs.GroupRatio);
        }
        if (originInputs['CompletionRatio'] !== inputs.CompletionRatio) {
          if (!verifyJSON(inputs.CompletionRatio)) {
            showError('补全倍率不是合法的 JSON 字符串');
            return;
          }
          await updateOption('CompletionRatio', inputs.CompletionRatio);
        }
        break;
      case 'quota':
        if (originInputs['QuotaForNewUser'] !== inputs.QuotaForNewUser) {
          await updateOption('QuotaForNewUser', inputs.QuotaForNewUser);
        }
        if (originInputs['QuotaForInvitee'] !== inputs.QuotaForInvitee) {
          await updateOption('QuotaForInvitee', inputs.QuotaForInvitee);
        }
        if (originInputs['QuotaForInviter'] !== inputs.QuotaForInviter) {
          await updateOption('QuotaForInviter', inputs.QuotaForInviter);
        }
        if (originInputs['PreConsumedQuota'] !== inputs.PreConsumedQuota) {
          await updateOption('PreConsumedQuota', inputs.PreConsumedQuota);
        }
        break;
      case 'general':
        if (originInputs['TopUpLink'] !== inputs.TopUpLink) {
          await updateOption('TopUpLink', inputs.TopUpLink);
        }
        if (originInputs['ChatLink'] !== inputs.ChatLink) {
          await updateOption('ChatLink', inputs.ChatLink);
        }
        if (originInputs['QuotaPerUnit'] !== inputs.QuotaPerUnit) {
          await updateOption('QuotaPerUnit', inputs.QuotaPerUnit);
        }
        if (originInputs['RetryTimes'] !== inputs.RetryTimes) {
          await updateOption('RetryTimes', inputs.RetryTimes);
        }
        break;
      case 'subscription':
        for (const code of PLAN_CODES) {
          for (const field of PLAN_FIELDS) {
            const key = `Plan${field}_${code}`;
            if (inputs[key] !== undefined && originInputs[key] !== inputs[key]) {
              await updateOption(key, String(inputs[key]));
            }
          }
        }
        if (originInputs['UsageDisplayMultiplier'] !== inputs.UsageDisplayMultiplier) {
          await updateOption('UsageDisplayMultiplier', inputs.UsageDisplayMultiplier);
        }
        break;
      case 'xunhupay':
        if (originInputs['XunhuPayAppID'] !== inputs.XunhuPayAppID) {
          await updateOption('XunhuPayAppID', inputs.XunhuPayAppID);
        }
        if (originInputs['XunhuPayAppSecret'] !== inputs.XunhuPayAppSecret) {
          await updateOption('XunhuPayAppSecret', inputs.XunhuPayAppSecret);
        }
        if (originInputs['XunhuPayNotifyURL'] !== inputs.XunhuPayNotifyURL) {
          await updateOption('XunhuPayNotifyURL', inputs.XunhuPayNotifyURL);
        }
        if (originInputs['XunhuPayGateway'] !== inputs.XunhuPayGateway) {
          await updateOption('XunhuPayGateway', inputs.XunhuPayGateway);
        }
        break;
    }
  };

  const updatePlanInput = (code, field, value) => {
    const key = `Plan${field}_${code}`;
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  const getPlanVal = (code, field) => {
    const key = `Plan${field}_${code}`;
    return inputs[key] || '';
  };

  // 模型白名单：DB 存 JSON 数组字符串，Dropdown 用数组
  const getPlanModels = (code) => {
    const raw = getPlanVal(code, 'Models');
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const setPlanModels = (code, models) => {
    updatePlanInput(code, 'Models', JSON.stringify(models));
  };

  // 价格字段在数据库里以"分"存储（PlanPrice_<code>），但前端输入/展示用"元"。
  // 渲染时把 cents → yuan；保存时把 yuan → cents。
  const getPlanPriceYuan = (code) => {
    const centsStr = getPlanVal(code, 'Price');
    if (centsStr === '' || centsStr === null || centsStr === undefined) return '';
    const cents = Number(centsStr);
    if (Number.isNaN(cents)) return '';
    // 直接除以 100，保留两位小数（整数时显示 "40"，而不是 "40.00"）
    return (cents / 100).toString();
  };

  const setPlanPriceYuan = (code, yuanValue) => {
    // 允许空值（清空），保存时会跳过
    if (yuanValue === '' || yuanValue === null || yuanValue === undefined) {
      updatePlanInput(code, 'Price', '');
      return;
    }
    const yuan = Number(yuanValue);
    if (Number.isNaN(yuan) || yuan < 0) {
      updatePlanInput(code, 'Price', '');
      return;
    }
    // 乘 100 转分，四舍五入到整数（避免浮点误差）
    const cents = Math.round(yuan * 100);
    updatePlanInput(code, 'Price', cents);
  };

  const deleteHistoryLogs = async () => {
    console.log(inputs);
    const res = await API.delete(
      `/api/log/?target_timestamp=${Date.parse(historyTimestamp) / 1000}`
    );
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(`${data} 条日志已清理！`);
      return;
    }
    showError('日志清理失败：' + message);
  };

  return (
    <Grid columns={1}>
      <Grid.Column>
        <Form loading={loading}>
          <Header as='h3'>{t('setting.operation.quota.title')}</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('setting.operation.quota.new_user')}
              name='QuotaForNewUser'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForNewUser}
              type='number'
              min='0'
              placeholder={t('setting.operation.quota.new_user_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.quota.pre_consume')}
              name='PreConsumedQuota'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.PreConsumedQuota}
              type='number'
              min='0'
              placeholder={t('setting.operation.quota.pre_consume_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.quota.inviter_reward')}
              name='QuotaForInviter'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForInviter}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.quota.inviter_reward_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.quota.invitee_reward')}
              name='QuotaForInvitee'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaForInvitee}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.quota.invitee_reward_placeholder'
              )}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('quota').then();
            }}
          >
            {t('setting.operation.quota.buttons.save')}
          </Form.Button>
          <Divider />
          <Header as='h3'>{t('setting.operation.ratio.title')}</Header>
          <Form.Group widths='equal'>
            <Form.TextArea
              label={t('setting.operation.ratio.model.title')}
              name='ModelRatio'
              onChange={handleInputChange}
              style={{ minHeight: 250, fontFamily: 'JetBrains Mono, Consolas' }}
              autoComplete='new-password'
              value={inputs.ModelRatio}
              placeholder={t('setting.operation.ratio.model.placeholder')}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.TextArea
              label={t('setting.operation.ratio.completion.title')}
              name='CompletionRatio'
              onChange={handleInputChange}
              style={{ minHeight: 250, fontFamily: 'JetBrains Mono, Consolas' }}
              autoComplete='new-password'
              value={inputs.CompletionRatio}
              placeholder={t('setting.operation.ratio.completion.placeholder')}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.TextArea
              label={t('setting.operation.ratio.group.title')}
              name='GroupRatio'
              onChange={handleInputChange}
              style={{ minHeight: 250, fontFamily: 'JetBrains Mono, Consolas' }}
              autoComplete='new-password'
              value={inputs.GroupRatio}
              placeholder={t('setting.operation.ratio.group.placeholder')}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('ratio').then();
            }}
          >
            {t('setting.operation.ratio.buttons.save')}
          </Form.Button>
          <Divider />
          <Header as='h3'>{t('setting.operation.log.title')}</Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.LogConsumeEnabled === 'true'}
              label={t('setting.operation.log.enable_consume')}
              name='LogConsumeEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths={4}>
            <Form.Input
              label={t('setting.operation.log.target_time')}
              value={historyTimestamp}
              type='datetime-local'
              name='history_timestamp'
              onChange={(e, { name, value }) => {
                setHistoryTimestamp(value);
              }}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              deleteHistoryLogs().then();
            }}
          >
            {t('setting.operation.log.buttons.clean')}
          </Form.Button>

          <Divider />
          <Header as='h3'>{t('setting.operation.monitor.title')}</Header>
          <Form.Group widths={3}>
            <Form.Input
              label={t('setting.operation.monitor.max_response_time')}
              name='ChannelDisableThreshold'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.ChannelDisableThreshold}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.monitor.max_response_time_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.monitor.quota_reminder')}
              name='QuotaRemindThreshold'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaRemindThreshold}
              type='number'
              min='0'
              placeholder={t(
                'setting.operation.monitor.quota_reminder_placeholder'
              )}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AutomaticDisableChannelEnabled === 'true'}
              label={t('setting.operation.monitor.auto_disable')}
              name='AutomaticDisableChannelEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.AutomaticEnableChannelEnabled === 'true'}
              label={t('setting.operation.monitor.auto_enable')}
              name='AutomaticEnableChannelEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('monitor').then();
            }}
          >
            {t('setting.operation.monitor.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3'>{t('setting.operation.general.title')}</Header>
          <Form.Group widths={4}>
            <Form.Input
              label={t('setting.operation.general.topup_link')}
              name='TopUpLink'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.TopUpLink}
              type='link'
              placeholder={t(
                'setting.operation.general.topup_link_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.general.chat_link')}
              name='ChatLink'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.ChatLink}
              type='link'
              placeholder={t('setting.operation.general.chat_link_placeholder')}
            />
            <Form.Input
              label={t('setting.operation.general.quota_per_unit')}
              name='QuotaPerUnit'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.QuotaPerUnit}
              type='number'
              step='0.01'
              placeholder={t(
                'setting.operation.general.quota_per_unit_placeholder'
              )}
            />
            <Form.Input
              label={t('setting.operation.general.retry_times')}
              name='RetryTimes'
              type={'number'}
              step='1'
              min='0'
              onChange={handleInputChange}
              autoComplete='new-password'
              value={inputs.RetryTimes}
              placeholder={t(
                'setting.operation.general.retry_times_placeholder'
              )}
            />
          </Form.Group>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.DisplayInCurrencyEnabled === 'true'}
              label={t('setting.operation.general.display_in_currency')}
              name='DisplayInCurrencyEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.DisplayTokenStatEnabled === 'true'}
              label={t('setting.operation.general.display_token_stat')}
              name='DisplayTokenStatEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.ApproximateTokenEnabled === 'true'}
              label={t('setting.operation.general.approximate_token')}
              name='ApproximateTokenEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Button
            onClick={() => {
              submitConfig('general').then();
            }}
          >
            {t('setting.operation.general.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3'>
            {t('setting.operation.subscription.title')}
          </Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.SubscriptionEnabled === 'true'}
              label={t('setting.operation.subscription.enable')}
              name='SubscriptionEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>

          {/* 全局订阅参数 */}
          <Header as='h4'>{t('setting.operation.subscription.global_title')}</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('setting.operation.subscription.usage_display_multiplier')}
              name='UsageDisplayMultiplier'
              value={inputs.UsageDisplayMultiplier}
              type='number'
              step='0.1'
              min='0.1'
              onChange={(_, { value }) => setInputs((prev) => ({ ...prev, UsageDisplayMultiplier: value }))}
            />
          </Form.Group>

          {/* 各套餐参数 */}
          <Header as='h4'>{t('setting.operation.subscription.plans_title')}</Header>
          {PLAN_CODES.map((code) => (
            <div key={code}>
              <Header as='h5' style={{ margin: '12px 0 8px 0', color: '#2185d0' }}>
                {t(`setting.operation.subscription.plans.${code}.name`)}
              </Header>
              <Form.Group widths='equal'>
                <Form.Input
                  label={t('setting.operation.subscription.price_cents')}
                  type='number'
                  step='0.01'
                  min='0'
                  value={getPlanPriceYuan(code)}
                  onChange={(_, { value }) => setPlanPriceYuan(code, value)}
                  placeholder={t('setting.operation.subscription.price_cents_placeholder')}
                />
                <Form.Input
                  label={t('setting.operation.subscription.monthly_quota')}
                  type='number'
                  step='1'
                  min='0'
                  value={getPlanVal(code, 'MonthlyQuota')}
                  onChange={(_, { value }) => updatePlanInput(code, 'MonthlyQuota', value)}
                />
                <Form.Input
                  label={t('setting.operation.subscription.request_limit_5h')}
                  type='number'
                  step='1'
                  min='0'
                  value={getPlanVal(code, 'RequestLimit5h')}
                  onChange={(_, { value }) => updatePlanInput(code, 'RequestLimit5h', value)}
                />
                <Form.Input
                  label={t('setting.operation.subscription.request_limit_week')}
                  type='number'
                  step='1'
                  min='0'
                  value={getPlanVal(code, 'RequestLimitWeek')}
                  onChange={(_, { value }) => updatePlanInput(code, 'RequestLimitWeek', value)}
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label={t('setting.operation.subscription.concurrency_limit')}
                  type='number'
                  step='1'
                  min='0'
                  value={getPlanVal(code, 'ConcurrencyLimit')}
                  onChange={(_, { value }) => updatePlanInput(code, 'ConcurrencyLimit', value)}
                />
                <Form.Select
                  label={t('setting.operation.subscription.quota_exhaust_strategy')}
                  options={[
                    { key: 'hard_cutoff', text: t('setting.operation.subscription.strategy_hard_cutoff'), value: 'hard_cutoff' },
                    { key: 'overage_billing', text: t('setting.operation.subscription.strategy_overage_billing'), value: 'overage_billing' },
                  ]}
                  value={getPlanVal(code, 'QuotaExhaustStrategy') || 'hard_cutoff'}
                  onChange={(_, { value }) => updatePlanInput(code, 'QuotaExhaustStrategy', value)}
                />
              </Form.Group>
              <Form.Dropdown
                label={t('setting.operation.subscription.models')}
                placeholder={t('setting.operation.subscription.models_placeholder')}
                fluid
                multiple
                search
                selection
                options={modelOptions}
                value={getPlanModels(code)}
                onChange={(_, { value }) => setPlanModels(code, value)}
              />
            </div>
          ))}
          <Form.Button
            onClick={() => {
              submitConfig('subscription').then(() => showSuccess(t('setting.operation.subscription.buttons.save') + ' ✓'));
            }}
          >
            {t('setting.operation.subscription.buttons.save')}
          </Form.Button>

          <Divider />
          <Header as='h3'>{t('setting.operation.payment.title')}</Header>
          <Form.Group inline>
            <Form.Checkbox
              checked={inputs.AlipayF2FEnabled === 'true'}
              label={t('setting.operation.payment.enable_alipay_f2f')}
              name='AlipayF2FEnabled'
              onChange={handleInputChange}
            />
            <Form.Checkbox
              checked={inputs.XunhuPayEnabled === 'true'}
              label={t('setting.operation.payment.enable_xunhupay')}
              name='XunhuPayEnabled'
              onChange={handleInputChange}
            />
          </Form.Group>

          <Header as='h4'>{t('setting.operation.payment.xunhupay_title')}</Header>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('setting.operation.payment.xunhupay_app_id')}
              placeholder={t('setting.operation.payment.xunhupay_app_id_placeholder')}
              name='XunhuPayAppID'
              value={inputs.XunhuPayAppID || ''}
              onChange={handleInputChange}
            />
            <Form.Input
              label={t('setting.operation.payment.xunhupay_app_secret')}
              placeholder={t('setting.operation.payment.xunhupay_app_secret_placeholder')}
              name='XunhuPayAppSecret'
              type='password'
              value={inputs.XunhuPayAppSecret || ''}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group widths='equal'>
            <Form.Input
              label={t('setting.operation.payment.xunhupay_notify_url')}
              placeholder={t('setting.operation.payment.xunhupay_notify_url_placeholder')}
              name='XunhuPayNotifyURL'
              value={inputs.XunhuPayNotifyURL || ''}
              onChange={handleInputChange}
            />
            <Form.Input
              label={t('setting.operation.payment.xunhupay_gateway')}
              placeholder='https://api.xunhupay.com'
              name='XunhuPayGateway'
              value={inputs.XunhuPayGateway || ''}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Message info size='small'>
            <p><strong>异步通知地址（NotifyURL / Async notify URL）</strong>：虎皮椒收到用户付款后向你配置的 URL 主动 POST 回调通知。99% 情况保持<strong>留空</strong>，代码会自动用「你的站点域名/api/payment/xunhupay/notify」。要求公网可访问的 HTTPS。详细说明见「开发者手册 §9.5 支付通道配置」。</p>
            <p style={{ marginTop: '0.5em' }}><strong>网关地址 (Gateway URL)</strong>：虎皮椒的服务器域名，你的后端调它来下单/查单/验签。99% 情况保持<strong>默认</strong> <code>https://api.xunhupay.com</code>（沙箱和生产同地址）。</p>
          </Message>
          <Form.Button
            onClick={() => {
              submitConfig('xunhupay').then(() => showSuccess(t('setting.operation.subscription.buttons.save') + ' ✓'));
            }}
          >
            {t('setting.operation.subscription.buttons.save')}
          </Form.Button>

          <Message info>
            <Message.Header>{t('setting.operation.payment.tip_title')}</Message.Header>
            <p>{t('setting.operation.payment.tip_body')}</p>
          </Message>

        </Form>
      </Grid.Column>
    </Grid>
  );
};

export default OperationSetting;
